import "./CampanaFormModal.css";
import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { CampanaResponse } from "../../../shared/response/marketing/CampanaResponse";
import { CampanaPayload } from "../../../shared/services/api/payload/marketing/CampanaPayload";
import { CampanaSaveService } from "../../services/marketing/CampanaSaveService";
import { CAMPANA_OBJETIVOS } from "./campanaConstants";

/**
 * Modal crear/editar campaña.
 * - Landing: código de activación + cupos + URL + activa demo.
 * - WhatsApp / Email: plantilla de mensaje (`mensajeTemplate`).
 * El backend guarda el registro de la campaña (tipo + mensajeTemplate); el **envío** y la
 * **audiencia** outbound no están implementados aún, por eso el modal no los incluye.
 * El 400 de código duplicado (landing) se muestra vía el base.
 */
export class CampanaFormModal extends BaseSmallModalFormComponent<
  CampanaResponse,
  CampanaPayload,
  CampanaSaveService
> {
  private _nombre!:   HTMLInputElement;
  private _tipo!:     HTMLSelectElement;
  private _objetivo!: HTMLSelectElement;
  private _codigo!:   HTMLInputElement;
  private _cupos!:    HTMLInputElement;
  private _fecha!:    HTMLInputElement;
  private _url!:      HTMLInputElement;
  private _demo!:     HTMLInputElement;
  private _mensaje!:  HTMLTextAreaElement;
  private _landingBox!: HTMLElement;
  private _msgBox!:     HTMLElement;

  constructor(
    private readonly _onSavedCb: (item: CampanaResponse) => void,
    private readonly _item?: CampanaResponse,
  ) {
    super(new CampanaSaveService());
  }

  protected get title(): string { return this._item ? "Editar campaña" : "Nueva campaña"; }
  protected get saveText(): string { return "Guardar campaña"; }

  private get _isLanding(): boolean { return (this._tipo?.value ?? this._item?.tipo ?? "landing") === "landing"; }

  protected renderBody(): string {
    const c = this._item;
    const tipo = c?.tipo ?? "landing";
    const fechaVal = c?.fechaLimite ? new Date(c.fechaLimite).toISOString().slice(0, 10) : "";
    const isLanding = tipo === "landing";

    return `
      <div class="cl-form-fields">
        <div class="cl-form-group">
          <label class="cl-form-label" for="cf-nombre">Nombre <span class="cl-required">*</span></label>
          <input id="cf-nombre" class="sx-input" type="text" maxlength="120" value="${this._esc(c?.nombre ?? "")}" placeholder="Ej: Feria DJI — Demos DATA AG">
        </div>

        <div class="cmp-form-row">
          <div class="cl-form-group">
            <label class="cl-form-label" for="cf-tipo">Tipo</label>
            <select id="cf-tipo" class="sx-input">
              <option value="landing"  ${tipo === "landing"  ? "selected" : ""}>Landing</option>
              <option value="whatsapp" ${tipo === "whatsapp" ? "selected" : ""}>WhatsApp</option>
              <option value="email"    ${tipo === "email"    ? "selected" : ""}>Email</option>
            </select>
          </div>
          <div class="cl-form-group">
            <label class="cl-form-label" for="cf-objetivo">Objetivo</label>
            <select id="cf-objetivo" class="sx-input">
              ${CAMPANA_OBJETIVOS.map((o) =>
                `<option value="${o.value}" ${c?.objetivo === o.value ? "selected" : ""}>${o.label}</option>`,
              ).join("")}
            </select>
          </div>
        </div>

        <div class="cl-form-group">
          <label class="cl-form-label" for="cf-fecha">Fecha límite</label>
          <input id="cf-fecha" class="sx-input" type="date" value="${fechaVal}">
          <span class="cmp-form-note">Vacío = sin fecha límite.</span>
        </div>

        <!-- Campos de LANDING -->
        <div class="cmp-landing-box" id="cf-landing-box" ${isLanding ? "" : "hidden"}>
          <div class="cmp-form-row">
            <div class="cl-form-group">
              <label class="cl-form-label" for="cf-codigo">Código de activación <span class="cl-required">*</span></label>
              <input id="cf-codigo" class="sx-input" type="text" maxlength="30" value="${this._esc(c?.codigo ?? "")}" placeholder="FERIA25" style="text-transform:uppercase;font-family:var(--sx-font-mono,monospace)">
            </div>
            <div class="cl-form-group">
              <label class="cl-form-label" for="cf-cupos">Cupos máximos</label>
              <input id="cf-cupos" class="sx-input sx-input--numeric" type="number" min="0" step="1" value="${c?.cuposMaximos ?? 0}">
              <span class="cmp-form-note">0 = sin límite.</span>
            </div>
          </div>
          <div class="cl-form-group">
            <label class="cl-form-label" for="cf-url">URL de la landing</label>
            <input id="cf-url" class="sx-input" type="text" value="${this._esc(c?.urlLanding ?? "")}" placeholder="https://sextantedrones.com/landing/…">
          </div>
          <label class="cmp-check">
            <input id="cf-demo" type="checkbox" ${c?.activaDemo ?? true ? "checked" : ""}>
            <span>Activa demo de 30 días al registrarse</span>
          </label>
        </div>

        <!-- Campos de WHATSAPP / EMAIL -->
        <div class="cmp-msg-box" id="cf-msg-box" ${isLanding ? "hidden" : ""}>
          <div class="cl-form-group">
            <label class="cl-form-label" for="cf-mensaje">Mensaje <span class="cl-required">*</span></label>
            <textarea id="cf-mensaje" class="sx-input" rows="4" style="resize:vertical" placeholder="Hola {{nombre}}, …">${this._esc(c?.mensajeTemplate ?? "")}</textarea>
            <span class="cmp-form-note">Variables: {{nombre}}, {{finca}}, {{hectareas}}. El envío se habilitará cuando la integración esté lista.</span>
          </div>
        </div>
      </div>
    `;
  }

  protected override onAfterRender(el: HTMLDivElement): void {
    this._nombre     = el.querySelector<HTMLInputElement>("#cf-nombre")!;
    this._tipo       = el.querySelector<HTMLSelectElement>("#cf-tipo")!;
    this._objetivo   = el.querySelector<HTMLSelectElement>("#cf-objetivo")!;
    this._codigo     = el.querySelector<HTMLInputElement>("#cf-codigo")!;
    this._cupos      = el.querySelector<HTMLInputElement>("#cf-cupos")!;
    this._fecha      = el.querySelector<HTMLInputElement>("#cf-fecha")!;
    this._url        = el.querySelector<HTMLInputElement>("#cf-url")!;
    this._demo       = el.querySelector<HTMLInputElement>("#cf-demo")!;
    this._mensaje    = el.querySelector<HTMLTextAreaElement>("#cf-mensaje")!;
    this._landingBox = el.querySelector<HTMLElement>("#cf-landing-box")!;
    this._msgBox     = el.querySelector<HTMLElement>("#cf-msg-box")!;

    this._tipo.addEventListener("change", () => {
      const landing = this._isLanding;
      this._landingBox.toggleAttribute("hidden", !landing);
      this._msgBox.toggleAttribute("hidden", landing);
      this._validate();
    });
    this._codigo.addEventListener("input", () => {
      const pos = this._codigo.selectionStart;
      this._codigo.value = this._codigo.value.toUpperCase();
      this._codigo.setSelectionRange(pos, pos);
      this._validate();
    });
    this._nombre.addEventListener("input", () => this._validate());
    this._mensaje.addEventListener("input", () => this._validate());
    this._validate();
  }

  protected buildPayload(): CampanaPayload {
    const fecha = this._fecha.value;
    const landing = this._isLanding;
    return {
      id: this._item?.id ?? null,
      nombre: this._nombre.value.trim(),
      tipo: this._tipo.value,
      objetivo: this._objetivo.value,
      fechaLimite: fecha ? new Date(fecha).toISOString() : null,
      // Landing:
      codigo: landing ? (this._codigo.value.trim() || null) : null,
      cuposMaximos: landing ? Math.max(0, Math.floor(Number(this._cupos.value) || 0)) : 0,
      urlLanding: landing ? (this._url.value.trim() || null) : null,
      activaDemo: landing ? this._demo.checked : false,
      // WhatsApp / Email:
      mensajeTemplate: landing ? null : (this._mensaje.value.trim() || null),
    };
  }

  protected override onSaved(item: CampanaResponse): void {
    this._onSavedCb(item);
    this.dispose();
  }

  /** Landing requiere nombre + código; WhatsApp/Email requieren nombre + mensaje. */
  private _validate(): void {
    const hasNombre = this._nombre.value.trim().length >= 1;
    const ok = this._isLanding
      ? hasNombre && this._codigo.value.trim().length >= 1
      : hasNombre && this._mensaje.value.trim().length >= 1;
    this.setSaveDisabled(!ok);
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  }
}
